import {isEmpty} from "lodash-es";
import {IServicePaymentDetails, IServicePaymentOption, servicePaymentType, IBooking, IBookingMenuOption, IServiceItem, IScheduleService} from "shared-types/index";
import ScheduleLookupService from 'external-services/schedule-lookup-service/scheduleLookup.service';
import ServiceDetectionService from "external-services/service-detection-service/serviceDetection.service";

const NS = 'MenuOptionReducerService';

export interface IMenuOptionReducerState {
  booking: IBooking;
  activeChildMenuOption: any;
  cachedMenuOptionDetails: any;
  activeService: any;
  editCachedMenuOptionDetails: {id: string}[];
  currency: string;
}

export interface IBookingOptionPayment {
  paymentTypeOverride: {paymentType: servicePaymentType, hasError: boolean};
  id: string;
}

class MenuOptionReducerService {

  private state: IMenuOptionReducerState;

  setState(newState: IMenuOptionReducerState): void {
    // this.state = newState;
    this.state = {...this.state, ...newState};
  }

  getState(): IMenuOptionReducerState {
    return this.state;
  }

  clearCachedBookingOptions(): void {
    const state = this.state;
    const newState: IMenuOptionReducerState = {
      ...state,
      cachedMenuOptionDetails: []
    }
    this.setState(newState);
  }

  async getSelectedService(): Promise<IServiceItem> {
    if (this.state.activeService) {
      return Promise.resolve(this.state.activeService);
    }
    // if no active service, then fetch it
    const {time} = this.state.booking;
    const schedule = await ScheduleLookupService.getSchedule(time);
    const service = ServiceDetectionService.getBookingService(this.state.booking, schedule);
    this.state.activeService = service;
    return service;
  }

  updateActiveChildMenuOption(payload: any): void {
    const menuOptionsImplicit = payload ? payload.implicitChildMenuOptions : null;
    const menuOptionsExplicit = payload ? payload.explicitChildMenuOptions : null;

    const state = this.state; //MenuOptionReducerService.getState();

    const activeChildMenuOption = {
      ...state.activeChildMenuOption,
      menuOptionsImplicit,
      menuOptionsExplicit
    };

    const cachedMenuOptionDetails = state.cachedMenuOptionDetails ? state.cachedMenuOptionDetails : [];

    (menuOptionsImplicit || []).concat(menuOptionsExplicit || []).forEach((opt: { id: any; }) => {
      const ind = cachedMenuOptionDetails.findIndex((val: any) => val.id === opt.id);
      if (ind > -1) {
        cachedMenuOptionDetails[ind] = opt;
      } else {
        cachedMenuOptionDetails.push(opt);
      }
    })

    const newState = {
      ...state,
      activeChildMenuOption,
      cachedMenuOptionDetails
    }
    this.setState(newState);
  }

  updateCachedMenuOptionDetails(payload: any): void {

    // Here we update `state.cachedMenuOptionDetails` incase we need to request this menu option again (saves unnecessary backend calls)

    const state = this.state; // MenuOptionReducerService.getState();

    const cachedMenuOptionDetails = payload.fullRefresh ? [] : state.cachedMenuOptionDetails;

    payload.options.forEach((opt: { id: any; }) => {
      const ind: number = cachedMenuOptionDetails.findIndex(({id}: { id: any }) => id === opt.id);
      if (ind > -1) {
        cachedMenuOptionDetails[ind] = opt;
      } else {
        cachedMenuOptionDetails.push(opt);
      }
    });

    const newState = {
      ...state,
      cachedMenuOptionDetails
    }

    this.setState(newState);
  }

  updateBookingOptionPayment(payload: IBookingOptionPayment): void {

    const state = this.state;// MenuOptionReducerService.getState();

    if (!state.activeService || !state.activeService.paymentDetails || !state.activeService.paymentDetails.options) {
      return;// {...state}
    }

    const options: IServicePaymentOption[] = state.activeService.paymentDetails.options.slice(); // creates a copy of the options array
    options.forEach(o => {
      o.paymentTypeOverride = o.id === payload.id ? payload.paymentTypeOverride : null;
    });

    const paymentDetails: IServicePaymentDetails = {
      ...state.activeService.paymentDetails,
      options
    };

    const newState = {
      ...state,
      activeService: {
        ...state.activeService,
        paymentDetails
      }
    }

    this.setState(newState);
  }

  updateMenuOptionExtras(payload: any): void {

    const {parentId, implicitChildMenuOptions, explicitChildMenuOptions, isSameForAll} = payload;

    const state = this.state; // MenuOptionReducerService.getState();

    const selectedMenuOptions = [
      ...state.booking.selectedMenuOptions
    ];
    const parentBookingOption = selectedMenuOptions.find(o => o.menuOptionId === parentId);

    if (parentBookingOption) {
      const hasChildItems = implicitChildMenuOptions.length || explicitChildMenuOptions.length;

      if (hasChildItems) {
        parentBookingOption.extras = {
          implicitChildMenuOptions,
          explicitChildMenuOptions,
          isSameForAll
        };
      } else {
        delete parentBookingOption.extras;
      }
    }

    const booking: IBooking = {
      ...state.booking,
      selectedMenuOptions
    }

    const newState = {
      ...state,
      booking
    }

    this.setState(newState);
  }

  updateSelectedMenuOption(payload: IBookingMenuOption[]): void {
    const state = this.state;// MenuOptionReducerService.getState();

    const selectedUpsellMenuOption = !isEmpty(state.booking.selectedUpsellOptions)  ? state.booking.selectedUpsellOptions.filter((s: IBookingMenuOption) => s.quantity > 0): [];
    const newPayload = payload.filter(s => s.quantity > 0);
    const newSelectedMenuOptions = [...selectedUpsellMenuOption, ...newPayload];

    const booking: IBooking = {
      ...state.booking,
      selectedMenuOptions: newSelectedMenuOptions
    };

    const newState = {
      ...state,
      booking
    }

    this.setState(newState);
  }

  updateSelectedUpsellMenuOption(payload: IBookingMenuOption[]) {
    const state = this.getState();

    const selectedMenuOption = state.booking.selectedMenuOptions.filter((s: IBookingMenuOption) => !s.isUpsellItem && s.quantity > 0);
    const newPayload = payload.filter(s => s.quantity > 0);
    const newSelectedMenuOptions = [...selectedMenuOption, ...newPayload];

    const booking: IBooking = {
      ...state.booking,
      selectedMenuOptions: newSelectedMenuOptions,
      selectedUpsellOptions: newPayload
    };

    const newState = {
      ...state,
      booking
    }

    this.setState(newState);
  }

  setBookingTime(time: Date): void {
    const booking: IBooking = {...this.state.booking, time};
    this.setState({...this.state, booking});
  }

  setActiveService(activeService: IScheduleService): void {
    const booking: IBooking = {...this.state.booking, serviceId: activeService.id};
    this.setState({...this.state, activeService, booking});
  }

  setEditCachedMenuOptionDetails(editCachedMenuOptionDetails: {id: string}[]): void {
    this.setState({...this.state, editCachedMenuOptionDetails});
  }

  setBooking(booking: IBooking): void {
    this.setState({...this.state, booking});
  }
}

const instance = new MenuOptionReducerService();
export default instance as MenuOptionReducerService;
